import { useMemo, useState } from 'react'
import { Send, RefreshCcw, Database } from 'lucide-react'
import { SUPABASE_ANON_KEY, SUPABASE_URL, supabase } from '../../../lib/supabaseClient'

const initialConfig = {
  enabled: true,
}

const initialSendForm = {
  number: '',
  message: '',
  refer: '',
}

export default function AdminSmsPanel() {
  const [activeSubTab, setActiveSubTab] = useState('config')
  const [config, setConfig] = useState(initialConfig)
  const [configMeta, setConfigMeta] = useState({ hasApiKey: null, updatedAt: '' })
  const [sendForm, setSendForm] = useState(initialSendForm)
  const [logs, setLogs] = useState([])
  const [statusFilter, setStatusFilter] = useState('todos')
  const [loadingConfig, setLoadingConfig] = useState(false)
  const [bootLoading, setBootLoading] = useState(false)
  const [sending, setSending] = useState(false)
  const [loadingLogs, setLoadingLogs] = useState(false)
  const [feedback, setFeedback] = useState({ type: '', message: '' })

  const smsSegments = useMemo(() => {
    const length = String(sendForm.message || '').length
    return Math.max(1, Math.ceil(length / 160))
  }, [sendForm.message])

  const callSms = async (payload, { requiresAuth = false } = {}) => {
    const base = String(SUPABASE_URL || '').replace(/\/+$/g, '')
    const headers = {
      'Content-Type': 'application/json',
      apikey: String(SUPABASE_ANON_KEY || ''),
    }

    if (requiresAuth) {
      const { data: sessionData } = await supabase.auth.getSession()
      const token = sessionData?.session?.access_token || null
      if (!token) {
        throw new Error('Sessão inválida para acessar SMS. Faça login novamente.')
      }
      headers.Authorization = `Bearer ${token}`
    }

    const res = await fetch(`${base}/functions/v1/send-sms`, {
      method: 'POST',
      headers,
      body: JSON.stringify(payload || {}),
    })

    const text = await res.text().catch(() => '')
    let parsed = null
    try {
      parsed = text ? JSON.parse(text) : null
    } catch {
      parsed = { raw: text }
    }

    if (!res.ok) {
      const message = String(parsed?.error || parsed?.message || text || `HTTP ${res.status}`)
      const authLikeError =
        res.status === 401 ||
        res.status === 403 ||
        message.toLowerCase().includes('unauthorized') ||
        message.toLowerCase().includes('forbidden') ||
        message.toLowerCase().includes('missing authorization header')
      if (authLikeError) {
        throw new Error('Sessão inválida para acessar SMS. Faça login novamente.')
      }
      throw new Error(message)
    }

    return parsed || {}
  }

  const loadConfig = async () => {
    try {
      setLoadingConfig(true)
      const result = await callSms({ action: 'getConfig' }, { requiresAuth: false })
      const cfg = result?.config || null
      if (cfg) {
        setConfig({
          enabled: cfg.enabled ?? true,
          serviceType: Number(cfg.serviceType || 9),
          senderName: cfg.senderName || '',
        })
        setConfigMeta({
          hasApiKey: typeof cfg.hasApiKey === 'boolean' ? cfg.hasApiKey : null,
          updatedAt: cfg.updatedAt || '',
        })
      } else {
        setConfig(initialConfig)
        setConfigMeta({ hasApiKey: null, updatedAt: '' })
      }
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao carregar configuração de SMS.' })
    } finally {
      setLoadingConfig(false)
    }
  }

  const loadLogs = async () => {
    try {
      setLoadingLogs(true)
      const result = await callSms({
        action: 'listLogs',
        limit: 100,
        status: statusFilter === 'todos' ? '' : statusFilter,
      }, { requiresAuth: true })
      setLogs(Array.isArray(result?.logs) ? result.logs : [])
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Erro ao carregar histórico de SMS.' })
    } finally {
      setLoadingLogs(false)
    }
  }

  const handleLoadData = async () => {
    try {
      setBootLoading(true)
      if (activeSubTab === 'history') {
        await loadLogs()
      } else {
        await loadConfig()
      }
    } finally {
      setBootLoading(false)
    }
  }

  const handleSendSms = async () => {
    if (!config.enabled) {
      setFeedback({ type: 'error', message: 'Envio manual desabilitado nesta tela.' })
      return
    }
    try {
      setSending(true)
      setFeedback({ type: '', message: '' })
      const result = await callSms({
        action: 'send',
        number: sendForm.number,
        message: sendForm.message,
        refer: sendForm.refer,
      }, { requiresAuth: true })
      const logWarning = result?.logPersisted === false
        ? ' SMS enviado, mas o histórico está temporariamente indisponível.'
        : ''
      setFeedback({
        type: 'success',
        message: `SMS enviado com sucesso. ID Provedor: ${result?.providerMessageId || 'N/D'}.${logWarning}`,
      })
      setSendForm(initialSendForm)
      await loadLogs()
    } catch (error) {
      setFeedback({ type: 'error', message: error.message || 'Falha ao enviar SMS.' })
    } finally {
      setSending(false)
    }
  }

  return (
    <div className="animate-fade-in">
      <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 mb-5">
        <h3 className="text-white font-bold text-base mb-1">Canal SMS (SMS Dev)</h3>
        <p className="text-slate-400 text-sm">
          Configure sua chave, envie mensagens manuais e acompanhe histórico.
        </p>
        <div className="mt-4">
          <button
            onClick={handleLoadData}
            disabled={bootLoading}
            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-xs font-bold disabled:opacity-60"
          >
            <Database size={14} />
            {bootLoading ? 'Carregando...' : 'Carregar Dados SMS'}
          </button>
        </div>
      </div>

      <div className="flex gap-4 border-b border-slate-800 mb-6 overflow-x-auto whitespace-nowrap">
        <button onClick={() => setActiveSubTab('config')} className={`pb-2 font-bold text-xs uppercase ${activeSubTab === 'config' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Configurações</button>
        <button onClick={() => setActiveSubTab('send')} className={`pb-2 font-bold text-xs uppercase ${activeSubTab === 'send' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Envio Manual</button>
        <button onClick={() => setActiveSubTab('history')} className={`pb-2 font-bold text-xs uppercase ${activeSubTab === 'history' ? 'text-white border-b-2 border-orange-500' : 'text-slate-500'}`}>Histórico</button>
      </div>

      {feedback.message && (
        <div className={`mb-4 rounded-lg border px-4 py-3 text-sm ${feedback.type === 'error' ? 'bg-rose-600/10 border-rose-600/40 text-rose-300' : 'bg-emerald-600/10 border-emerald-600/40 text-emerald-300'}`}>
          {feedback.message}
        </div>
      )}

      {activeSubTab === 'config' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="rounded-lg border border-slate-800 bg-slate-950 px-3 py-3">
            <div className="text-xs font-bold text-slate-400 uppercase tracking-widest">Secret do Supabase</div>
            <div className="text-sm text-white mt-1">`SMSDEV_API_KEY`</div>
            <div className={`text-xs mt-2 ${configMeta.hasApiKey === true ? 'text-emerald-400' : configMeta.hasApiKey === false ? 'text-rose-400' : 'text-amber-300'}`}>
              {configMeta.hasApiKey === true
                ? 'Secret configurado no backend.'
                : configMeta.hasApiKey === false
                  ? 'Secret não encontrado. Configure no Supabase.'
                  : 'Clique em Carregar Dados SMS para validar o secret.'}
            </div>
          </div>

          <div className="flex items-center justify-between gap-3 mt-4">
            <label className="flex items-center gap-2 text-sm text-slate-300">
              <input
                type="checkbox"
                checked={Boolean(config.enabled)}
                onChange={(e) => setConfig((prev) => ({ ...prev, enabled: e.target.checked }))}
              />
              Habilitar envio manual na tela
            </label>
          </div>

          {loadingConfig && <p className="text-slate-500 text-xs mt-3">Carregando configuração...</p>}
          <p className="text-slate-500 text-xs mt-3">
            Não usamos tabela de configuração para chave de API. A chave fica somente em secret do Supabase.
          </p>
        </div>
      )}

      {activeSubTab === 'send' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <label className="flex flex-col gap-2 text-sm">
              <span className="text-slate-400">Número destino</span>
              <input
                type="text"
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white outline-none focus:border-white"
                value={sendForm.number}
                onChange={(e) => setSendForm((prev) => ({ ...prev, number: e.target.value }))}
                placeholder="5511988887777"
              />
            </label>
            <label className="flex flex-col gap-2 text-sm">
              <span className="text-slate-400">Referência (opcional)</span>
              <input
                type="text"
                maxLength={100}
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white outline-none focus:border-white"
                value={sendForm.refer}
                onChange={(e) => setSendForm((prev) => ({ ...prev, refer: e.target.value }))}
                placeholder="pedido-123"
              />
            </label>
          </div>
          <label className="flex flex-col gap-2 text-sm">
            <span className="text-slate-400">Mensagem</span>
            <textarea
              className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white outline-none focus:border-white min-h-[140px]"
              value={sendForm.message}
              onChange={(e) => setSendForm((prev) => ({ ...prev, message: e.target.value }))}
              placeholder="Digite a mensagem SMS"
            />
          </label>
          <div className="flex items-center justify-between mt-3">
            <p className="text-xs text-slate-500">
              Caracteres: {String(sendForm.message || '').length} · Créditos estimados: {smsSegments}
            </p>
            <button
              onClick={handleSendSms}
              disabled={sending}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-white font-bold text-sm disabled:opacity-60"
            >
              <Send size={16} />
              {sending ? 'Enviando...' : 'Enviar SMS'}
            </button>
          </div>
        </div>
      )}

      {activeSubTab === 'history' && (
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-5">
          <div className="flex flex-col md:flex-row gap-3 md:items-center md:justify-between mb-4">
            <div className="flex gap-2 items-center">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-white text-sm outline-none focus:border-white"
              >
                <option value="todos">Todos</option>
                <option value="Enviado">Enviado</option>
                <option value="Falha">Falha</option>
              </select>
            </div>
            <button
              onClick={() => loadLogs()}
              disabled={loadingLogs}
              className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-slate-800 hover:bg-slate-700 text-white text-sm font-bold"
            >
              <RefreshCcw size={14} />
              Atualizar
            </button>
          </div>

          {loadingLogs && <p className="text-slate-500 text-sm">Carregando histórico...</p>}
          {!loadingLogs && logs.length === 0 && (
            <p className="text-slate-500 text-sm">Nenhum envio encontrado.</p>
          )}

          {!loadingLogs && logs.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-left text-xs">
                <thead className="text-slate-400 uppercase">
                  <tr>
                    <th className="py-2 pr-3">Data</th>
                    <th className="py-2 pr-3">Destino</th>
                    <th className="py-2 pr-3">Status</th>
                    <th className="py-2 pr-3">Mensagem</th>
                    <th className="py-2 pr-3">ID Provedor</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-800/60">
                  {logs.map((item) => (
                    <tr key={item.id} className="text-slate-300">
                      <td className="py-2 pr-3 whitespace-nowrap">{new Date(item.created_at).toLocaleString('pt-BR')}</td>
                      <td className="py-2 pr-3">{item.recipient}</td>
                      <td className="py-2 pr-3">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold ${item.status === 'Enviado' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {item.status}
                        </span>
                      </td>
                      <td className="py-2 pr-3 max-w-[360px] truncate" title={item.message}>{item.message}</td>
                      <td className="py-2 pr-3">{item.provider_message_id || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  )
}
